// Helpers for the billing address of a subscription.
//
// The address is stored as a jsonb hash (keys are camelCase on the client,
// snake_case on the server) and is edited as one ';'-separated line:
//
//   street ; house number ; bus ; postal code ; city ; country
//
// Keys outside of these six (e.g. enterpriseNumber) are left untouched.

export const ADDRESS_KEYS = ['street', 'houseNumber', 'bus', 'postalCode', 'city', 'country']

const SNAKE_KEYS = {
  houseNumber: 'house_number',
  postalCode: 'postal_code'
}

const clean = (value) => (value == null ? '' : String(value)).replace(/\s+/g, ' ').trim()

// Server data arrives camelCased, but be forgiving about snake_case.
const read = (address, key) => clean(address?.[key] ?? address?.[SNAKE_KEYS[key]])

// "Kerkstraat ; 12 ; 3 ; 9000 ; Gent" -> { street, houseNumber, bus, postalCode, city }
// Segments are trimmed (spaces and newlines); empty ones are left out.
export function parseAddress (text) {
  const segments = String(text || '').split(';').map(clean)
  const address = {}
  ADDRESS_KEYS.forEach((key, index) => {
    if (segments[index]) address[key] = segments[index]
  })
  return { address, extraSegments: segments.slice(ADDRESS_KEYS.length).filter(Boolean) }
}

// Inverse of parseAddress, used to pre-fill the paste box.
export function formatAddress (address) {
  const values = ADDRESS_KEYS.map((key) => read(address, key))
  while (values.length && !values[values.length - 1]) values.pop()
  return values.join(' ; ')
}

// Replace the six managed keys of `existing`, keeping every other key.
export function mergeAddress (existing, parsed) {
  const kept = { ...(existing || {}) }
  ADDRESS_KEYS.forEach((key) => {
    delete kept[key]
    delete kept[SNAKE_KEYS[key]]
  })
  return { ...kept, ...parsed }
}

// Human readable lines, e.g. ["Kerkstraat 12 bus 3", "9000 Gent", "BE"].
export function addressLines (address) {
  const [street, houseNumber, bus, postalCode, city, country] = ADDRESS_KEYS.map((key) => read(address, key))
  return [
    [street, houseNumber, bus && `bus ${bus}`].filter(Boolean).join(' '),
    [postalCode, city].filter(Boolean).join(' '),
    country
  ].filter(Boolean)
}
